import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../api';
import { useCart } from '../context/CartContext';
import { clearActiveOrder } from '../lib/activeOrder';
import MenuItem from '../components/MenuItem';
import './MenuPage.css';

const VENUE_LABELS = {
  kitchen: 'Kitchen',
  cafe: 'Cafe',
  desserts: 'Desserts',
};

export default function MenuPage() {
  const [venues, setVenues] = useState([]);
  const [activeVenue, setActiveVenue] = useState(null);
  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [categoryWindows, setCategoryWindows] = useState({});
  const [acceptingOrders, setAcceptingOrders] = useState(true);
  const [appOrderMode, setAppOrderMode] = useState('on');
  const [pendingPath, setPendingPath] = useState(() => localStorage.getItem('active_order_path'));
  const { itemCount, total } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    api
      .getVenues()
      .then((v) => {
        setVenues(v);
        if (v.length > 0) setActiveVenue(v[0]);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
    api.getMenuStatus()
      .then((s) => {
        setAcceptingOrders(!!s.accepting_orders);
        setAppOrderMode(s.app_order_mode || (s.accepting_orders ? 'on' : 'off'));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!activeVenue) return;
    setLoading(true);
    setCategoryWindows({});
    api
      .getMenu(activeVenue)
      .then((data) => {
        setMenu(data);
        if (data.length > 0) setActiveCategory(data[0].id);
        else setActiveCategory(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));

    if (activeVenue === 'cafe') {
      api.getPublicCategoryWindows()
        .then((w) => setCategoryWindows(w || {}))
        .catch(() => setCategoryWindows({}));
    }
  }, [activeVenue]);

  useEffect(() => {
    const group = localStorage.getItem('active_order_group');
    const path = localStorage.getItem('active_order_path');
    if (!group && !path) return;

    const dismiss = () => {
      clearActiveOrder();
      setPendingPath(null);
    };

    if (!group) {
      dismiss();
      return;
    }

    api.getOrderGroup(group)
      .then((data) => {
        const orders = Array.isArray(data) ? data : [data];
        if (orders.length === 0) {
          dismiss();
          return;
        }
        const allDone = orders.every((o) => o.status === 'collected' || o.status === 'cancelled');
        if (allDone) {
          dismiss();
          return;
        }
        const live = orders.find((o) =>
          o.order_group
          && o.status !== 'awaiting_payment'
          && o.status !== 'cancelled'
        );
        if (live?.order_group && live.order_group !== group) {
          const nextPath = `/confirmation/group/${live.order_group}`;
          localStorage.setItem('active_order_group', live.order_group);
          localStorage.setItem('active_order_path', nextPath);
          if (live.customer_name) {
            localStorage.setItem('active_order_group_owner', String(live.customer_name).trim().toLowerCase());
          }
          setPendingPath(nextPath);
        }
      })
      .catch(() => {
        // Group not found (common after the daily period rolls over).
        // Fall back to single-order lookup for checkouts that stored an
        // order number instead of a group id; clear if that fails too.
        api.getOrder(group)
          .then((data) => {
            if (!data || data.status === 'collected' || data.status === 'cancelled') {
              dismiss();
            }
          })
          .catch(() => dismiss());
      });
  }, []);

  if (loading && venues.length === 0) {
    return (
      <div className="container page-center">
        <div className="spinner" />
        <p>Loading menu...</p>
      </div>
    );
  }

  if (error && venues.length === 0) {
    return (
      <div className="container page-center">
        <p className="error-text">{error}</p>
        <button className="btn btn-primary" onClick={() => window.location.reload()}>
          Try Again
        </button>
      </div>
    );
  }

  return (
    <main className={`menu-page${activeVenue === 'kitchen' ? ' menu-page-kitchen' : ''}`}>
      {!acceptingOrders && (
        <div className="orders-off-banner">
          Orders are currently closed
        </div>
      )}
      {acceptingOrders && appOrderMode === 'drinks' && (
        <div className="orders-drinks-banner">
          Drinks only — kitchen, desserts, and cafe food are at the counter
        </div>
      )}

      {pendingPath && (
        <Link to={pendingPath} className="pending-order-banner">
          <span>You have a pending order</span>
          <span className="pending-order-banner-action">Tap to view &rarr;</span>
        </Link>
      )}

      <div className="menu-sticky-nav">
        {venues.length > 1 && (
          <div className="venue-tabs">
            <div className="venue-tabs-inner container">
              {venues.map((v) => (
                <button
                  key={v}
                  className={`venue-tab ${activeVenue === v ? 'active' : ''}`}
                  onClick={() => setActiveVenue(v)}
                >
                  {VENUE_LABELS[v] || v}
                </button>
              ))}
            </div>
          </div>
        )}

        {menu.length > 0 && activeVenue !== 'kitchen' && (
          <div className="category-tabs">
            <div className="category-tabs-inner">
              {menu.map((cat) => (
                <button
                  key={cat.id}
                  className={`category-tab ${activeCategory === cat.id ? 'active' : ''}`}
                  onClick={() => {
                    setActiveCategory(cat.id);
                    document.getElementById(`cat-${cat.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="container page-center" style={{ minHeight: '30vh' }}>
          <div className="spinner" />
          <p>Loading menu...</p>
        </div>
      ) : menu.length === 0 ? (
        <div className="container page-center" style={{ minHeight: '30vh' }}>
          <p style={{ color: 'var(--gray-400)' }}>No items available right now</p>
        </div>
      ) : (
        <div className="container menu-list">
          {menu.map((cat) => {
            const catWindow = categoryWindows[cat.name];
            const isRestricted = catWindow && !catWindow.is_available;
            const isClosed = isRestricted && (() => {
              const now = new Date();
              const nowMins = now.getHours() * 60 + now.getMinutes();
              const [uh, um] = (catWindow.available_until || '15:00').split(':').map(Number);
              return nowMins >= (uh * 60 + um);
            })();
            const formatWindowTime = (t) => {
              const [h, m] = t.split(':').map(Number);
              const ampm = h >= 12 ? 'PM' : 'AM';
              return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
            };
            return (
              <section key={cat.id} id={`cat-${cat.id}`} className={`menu-section${isClosed ? ' menu-section-restricted' : ''}`}>
                <h2 className="menu-section-title">{cat.name}</h2>
                {isRestricted && (
                  <div className="menu-window-banner">
                    {isClosed
                      ? `${cat.name} orders have closed for today (available until ${formatWindowTime(catWindow.available_until || '15:00')})`
                      : `Available from ${formatWindowTime(catWindow.available_from)} — you can order for later`}
                  </div>
                )}
                <div className="menu-section-items">
                  {cat.items.map((item) => (
                    <MenuItem
                      key={item.id}
                      item={item}
                      disabled={isClosed || !acceptingOrders}
                      disabledLabel={!acceptingOrders ? 'Orders Closed' : 'Order for Later'}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {itemCount > 0 && (
        <div className="cart-bar" onClick={() => navigate('/cart')}>
          <div className="cart-bar-inner container">
            <div className="cart-bar-info">
              <span className="cart-bar-count">{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
              <span className="cart-bar-total">${total.toFixed(2)}</span>
            </div>
            <span className="cart-bar-action">View Cart &rarr;</span>
          </div>
        </div>
      )}
    </main>
  );
}
